import { useCallback, useMemo, useState } from "react";

import { fetchAppointmentTypes, type ModMedAppointmentTypeView } from "@/services/modmed";

import { CopyMenu } from "../components/CopyMenu";
import { Panel, PanelToolbar } from "../components/Panel";
import { EmptyRow, IdBadge, TableContainer, TBody, TD, TH, THead, TR } from "../components/Table";
import { usePanelFetch } from "../hooks";

const DISABLED_REASON = "Save the document before fetching ModMed resources.";

type Props = { customerId?: string; disabled: boolean };

export const AppointmentTypesPanel = ({ customerId, disabled }: Props) => {
  const [search, setSearch] = useState("");

  const fetcher = useCallback(() => fetchAppointmentTypes(customerId!), [customerId]);
  const { state, run } = usePanelFetch<ModMedAppointmentTypeView>(fetcher);

  const filtered = useMemo(() => {
    if (!state.results) return null;
    const q = search.trim().toLowerCase();
    if (!q) return state.results;
    return state.results.filter(
      (t) => t.id.toLowerCase().includes(q) || t.name.toLowerCase().includes(q),
    );
  }, [state.results, search]);

  return (
    <Panel
      title="Appointment Types"
      count={state.results?.length}
      disabled={disabled}
      disabledReason={DISABLED_REASON}
      toolbar={
        <PanelToolbar
          search={search}
          onSearchChange={setSearch}
          fetchLabel="Fetch appointment types"
          onFetch={run}
          disabled={disabled}
          state={state}
        />
      }
    >
      {filtered !== null && (
        <TableContainer>
          <THead>
            <tr>
              <TH className="w-36">ID (Code)</TH>
              <TH>Display Name</TH>
              <TH className="w-10" />
            </tr>
          </THead>
          <TBody>
            {filtered.length === 0 ? (
              <EmptyRow colSpan={3} label="appointment types" />
            ) : (
              filtered.map((type) => (
                <TR key={type.id}>
                  <TD>
                    <IdBadge value={type.id} />
                  </TD>
                  <TD className="font-medium">{type.name}</TD>
                  <TD>
                    <CopyMenu
                      options={[
                        { label: "ID (Code)", value: type.id },
                        { label: "Name", value: type.name },
                      ]}
                    />
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </TableContainer>
      )}
    </Panel>
  );
};
